import React, { useState, useContext, useEffect } from "react";
import { Route, Switch } from "react-router-dom";
import {
  ReferrerDetector,
  ScrollRestoration,
  PhraseAppToolbar,
  StateValidator,
  YearSwitchBanner,
  Footer,
} from "../components";
import { InjectIntlContext, isOutsideOpenEnrollment } from "../helpers";
import * as Pages from "../pages";
import MaintenancePage from "../components/MaintenancePage";
import { IntlProvider, addLocaleData } from "react-intl";
import messages_en from "../translations/en-US.json";
import messages_es from "../translations/es-MX.json";
import Routes from "./routes";
import { useFlags } from "launchdarkly-react-client-sdk";
import { AppStore } from "./store";
import en from "react-intl/locale-data/en";
import es from "react-intl/locale-data/es";
import RouteToastHandler from "../components/RouteToastHandler";
import UserActivityMonitor from "../components/UserActivityMonitor";

const AppContent: React.FunctionComponent = () => {
  const flags = useFlags();
  const { state } = useContext(AppStore.AppContext);

  const {
    mctYpPublicPreview,
    mctYpOpenEnrollment,
    mctYpOeNextYear,
    mctYpOutsideOpenEnrollment,
    fePhraseappToolbar,
    feMaintenanceMode,
    feEnableAuthentication,
    tmpPlanResultsSpike,
    tmpFeEnableNewCoverageWizard,
  } = flags;
  const outsideOpenEnrollment = isOutsideOpenEnrollment({
    mctYpPublicPreview,
    mctYpOpenEnrollment,
    mctYpOeNextYear,
    mctYpOutsideOpenEnrollment,
  });

  // The IntlProvider adds language localization to its children with a default language and traslations map as props
  const messages = {
    en: messages_en,
    es: messages_es,
  };

  addLocaleData([...en, ...es]);

  const [
    refreshComponentsForTranslation,
    setRefreshComponentsForTranslation,
  ] = useState(false);

  const [ldFlagTimeout, setLdFlagTimeout] = useState<
    NodeJS.Timeout | undefined
  >();
  const [couldNotRetrieveLdFlags, setCouldNotRetrieveLdFlags] = useState(false);
  const [ldFlagsAvailable, setLdFlagsAvailable] = useState(false);

  useEffect(() => {
    const ldFlagsAreAvailable = Object.getOwnPropertyNames(flags).length > 0;

    setLdFlagsAvailable(ldFlagsAreAvailable);

    if (!ldFlagsAreAvailable) {
      setLdFlagTimeout(
        setTimeout(() => {
          setCouldNotRetrieveLdFlags(true);
        }, 5000)
      );
    } else {
      if (ldFlagTimeout) {
        clearTimeout(ldFlagTimeout);
        setLdFlagTimeout(undefined);
      }

      setCouldNotRetrieveLdFlags(false);
    }
  }, [flags]);

  return (
    <IntlProvider
      locale={state.language}
      messages={messages[state.language] as object}
    >
      <InjectIntlContext>
        <StateValidator>
          <ScrollRestoration>
            <ReferrerDetector>
              <UserActivityMonitor>
                <RouteToastHandler>
                  <div className="ds-base">
                    {fePhraseappToolbar && (
                      <PhraseAppToolbar
                        onTranslationEnabled={(): void => {
                          // This method will hide components and force them to re-render so that PhraseApp can read the translation keys
                          setRefreshComponentsForTranslation(true);

                          setTimeout(() =>
                            setRefreshComponentsForTranslation(false)
                          );
                        }}
                      />
                    )}

                    {!outsideOpenEnrollment && !mctYpOeNextYear && (
                      <YearSwitchBanner />
                    )}

                    {!refreshComponentsForTranslation &&
                    (ldFlagsAvailable || couldNotRetrieveLdFlags) ? (
                      <div className="AppContent">
                        <main className="app__main">
                          {feMaintenanceMode || couldNotRetrieveLdFlags ? (
                            <MaintenancePage />
                          ) : (
                            <Switch>
                              <Route
                                path={Routes.searchResults}
                                component={Pages.SearchResultsPage}
                              />
                              {tmpPlanResultsSpike && (
                                <Route
                                  path="/spike"
                                  component={Pages.SearchResultsSpike}
                                />
                              )}

                              <Route
                                path={Routes.sanctionedPlans}
                                component={Pages.SanctionedPlansPage}
                              />

                              <Route
                                path={Routes.drugSearchPreferences}
                                component={Pages.DrugSearchPreferencesPage}
                              />

                              <Route
                                path={Routes.comparePlans}
                                component={Pages.PlanComparisonPage}
                              />

                              <Route
                                path={Routes.planDetails}
                                component={Pages.PlanDetailsPage}
                              />

                              <Route
                                path={Routes.managePrescriptions}
                                component={Pages.ManagePrescriptionsPage}
                              />
                              <Route
                                path={Routes.prescriptionsList}
                                component={Pages.ManagePrescriptionsPage}
                              />

                              <Route
                                path={Routes.pharmacy}
                                component={Pages.SelectPharmaciesPage}
                              />

                              <Route
                                path={Routes.coverageSelection}
                                component={Pages.CoverageSelectionPage}
                              />

                              <Route
                                path={Routes.slsCallback}
                                component={Pages.LoginCallbackHandler}
                              />

                              {feEnableAuthentication && (
                                <Route
                                  path={Routes.beneLanding}
                                  component={Pages.BeneLandingPage}
                                />
                              )}

                              <Route
                                path={Routes.questionRouting}
                                component={Pages.QuestionRoutingPage}
                              />

                              <Route
                                path={Routes.enroll}
                                component={Pages.EnrollmentFormPage}
                              />

                              <Route
                                path={Routes.csrLanding}
                                component={Pages.CSRLandingPage}
                              />

                              <Route
                                path={Routes.pdePage}
                                component={Pages.PDEPage}
                              />

                              <Route
                                path={Routes.mbpLandingPage}
                                component={Pages.MBPLandingPage}
                              />

                              <Route
                                path={Routes.version}
                                component={Pages.VersionPage}
                              />

                              <Route
                                path={Routes.planPreview}
                                component={Pages.PlanPreviewPage}
                              />

                              {tmpFeEnableNewCoverageWizard && (
                                <Route
                                  path={Routes.coverageWizard.options}
                                  component={Pages.CoverageWizardOptionsPage}
                                />
                              )}

                              {tmpFeEnableNewCoverageWizard && (
                                <Route
                                  path={Routes.coverageWizard.info}
                                  component={Pages.CoverageWizardInfoPage}
                                />
                              )}

                              {tmpFeEnableNewCoverageWizard && (
                                <Route
                                  path={Routes.coverageWizard.landingPage}
                                  component={Pages.CoverageWizardLandingPage}
                                />
                              )}

                              <Route
                                path={Routes.medigap.plans}
                                component={Pages.MedigapPlans}
                              />

                              <Route
                                path={Routes.medigap.planDetails}
                                component={Pages.MedigapPlanDetailsPage}
                              />

                              <Route
                                path={Routes.medigap.policies}
                                component={Pages.MedigapPoliciesPage}
                              />

                              <Route
                                path={Routes.medigap.landingPage}
                                component={Pages.MedigapLandingPage}
                              />

                              <Route component={Pages.LandingPage} />
                            </Switch>
                          )}
                        </main>

                        {!location.hash.includes(
                          Routes.planPreview.replace("/:id", "")
                        ) && <Footer />}
                      </div>
                    ) : (
                      <Pages.LoadingPage />
                    )}
                  </div>
                </RouteToastHandler>
              </UserActivityMonitor>
            </ReferrerDetector>
          </ScrollRestoration>
        </StateValidator>
      </InjectIntlContext>
    </IntlProvider>
  );
};

export default AppContent;
